import { ChangeEvent, MutableRefObject, Ref, useState } from "react";
import { Constraints, Combination, Result, BarData, UnmetDemands } from "../../types";
import { Column, RowsChangeData, textEditor } from "react-data-grid";
import { exportExcel, readExcel } from "../../services";
import { create2DArray } from "../../utils";
import _, { set } from "lodash";
import { UserSerie } from "react-charts";

// Define columns for the DataGrid
const columns: readonly Column<Combination>[] = [
    { key: "prep", name: "Prep.", renderEditCell: textEditor },
    { key: "line", name: "Line", renderEditCell: textEditor },
    { key: "market", name: "Market", renderEditCell: textEditor },
    { key: "itemCode", name: "item Code", renderEditCell: textEditor },
    { key: "description", name: "Description", renderEditCell: textEditor },
    { key: "variable", name: "Variable", renderEditCell: textEditor },
    { key: "profit", name: "Profit", renderEditCell: textEditor },
    { key: "demand", name: "Demand", renderEditCell: textEditor },
    { key: "prepCapacity", name: "Prep. Capacity", renderEditCell: textEditor },
    { key: "capacity", name: "Capacity", renderEditCell: textEditor },
    { key: "prep_max_capacity", name: "Prep. Max. Capacity", renderEditCell: textEditor },
    { key: "max_capacity", name: "Max. Capacity", renderEditCell: textEditor },
];

const unmetDemandsColumns: readonly Column<UnmetDemands>[] = [
    { key: "market", name: "Market" },
    { key: "item", name: "item Code" },
    { key: "demand", name: "Demand" },
    { key: "unmet", name: "Unmet Demand" },
];

const useCombinationsLogic = () => {
    // Initialize Combinations as an empty array
    const [combinations, setCombinations] = useState<Record<string, Combination>>({});

    const [output1, setOutput1] = useState<string>("");
    const [output2, setOutput2] = useState<string>("");
    const [output3, setOutput3] = useState<string>("");
    const [output4, setOutput4] = useState<string>("");
    const [linesUtilGraphData, setLineUtilGraphData] = useState<UserSerie<BarData>[]>([]);
    const [prepsUtilGraphData, setPrepUtilGraphData] = useState<UserSerie<BarData>[]>([]);
    const [unmetDemands, setUnmetDemands] = useState<UnmetDemands[]>([]);

    const [importLoading, setImportLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const [result, setResult] = useState<Result>({
        optimal_values: "",
        message: "",
        optimal_value: 0,
    });

    const [isFormulationsHidden, setIsFormulationsHidden] = useState(true);

    const generateCombinations = (values: any) => {
        // Generate combinations based on the user's input
        const newPreps = Array.from({ length: values.numberOfPreps }, (_, index) => `Prep line ${index + 1}`);
        const newFactories = Array.from({ length: values.numberOfFactories }, (_, index) => `line ${index + 1}`);
        const newMarkets = Array.from({ length: values.numberOfMarkets }, (_, index) => `Market ${index + 1}`);
        const newItems = Array.from({ length: values.numberOfItems }, (_, index) => `itemCode ${index + 1}`);

        // Create a data grid using these arrays
        const newCombinations: { [key: string]: Combination } = {};
        //damand of each itemCode in each market
        const demands = create2DArray(newMarkets.length, newItems.length, 1, 10);
        //capacity of each prep line per itemCode
        const prepCapacities = create2DArray(newPreps.length, newItems.length, 100, 500);
        //capacity of each line per itemCode
        const capacities = create2DArray(newFactories.length, newItems.length, 100, 500);
        //max capacity of each prep line
        const prepMaxCapacities = newPreps.map(() => Math.floor(Math.random() * 1000) + 100);
        //max capacity of each line
        const maxCapacities = newFactories.map(() => Math.floor(Math.random() * 1000) + 100);

        if (newPreps.length === 0) newPreps.push("");

        for (let prepIndex = 0; prepIndex < newPreps.length; prepIndex++) {
            const prep = newPreps[prepIndex];
            for (let lineIndex = 0; lineIndex < newFactories.length; lineIndex++) {
                const line = newFactories[lineIndex];
                for (let marketIndex = 0; marketIndex < newMarkets.length; marketIndex++) {
                    const market = newMarkets[marketIndex];
                    for (let itemCodeIndex = 0; itemCodeIndex < newItems.length; itemCodeIndex++) {
                        const itemCode = newItems[itemCodeIndex];
                        const variable = `x${Object.keys(newCombinations).length + 1}`;

                        newCombinations[variable] = {
                            prepIndex,
                            prep,
                            lineIndex,
                            line,
                            marketIndex,
                            market,
                            itemCodeIndex,
                            itemCode,
                            description: itemCode,
                            variable,
                            profit: Math.round(Math.random() * 10 + 1),
                            demand: demands[marketIndex][itemCodeIndex],
                            prepCapacity: prepCapacities.length > 0 ? prepCapacities[prepIndex][itemCodeIndex] : "",
                            capacity: capacities[lineIndex][itemCodeIndex],
                            prep_max_capacity: prepMaxCapacities.length > 0 ? prepMaxCapacities[prepIndex] : "",
                            max_capacity: maxCapacities[lineIndex],
                            variableValue: null,
                        };
                    }
                }
            }
        }
        // Now 'combinations' contains the data for the data grid
        setCombinations(newCombinations);
    };

    const generateOutputs = async () => {
        try {
            setExportLoading(true);
            const permArray = Object.values(combinations);
            const newOutput1 = permArray.reduce((prev, curr) => {
                if (prev === "") return `${curr.profit}*${curr.variable}`;
                return `${prev} + ${curr.profit}*${curr.variable}`;
            }, "");

            const commonDemands: { [key: string]: Constraints } = {};
            const commonCapacity: { [key: string]: Constraints } = {};
            const commonPrepCapacity: { [key: string]: Constraints } = {};

            for (let index = 0; index < permArray.length; index++) {
                const perm = permArray[index];
                const demandCommonIndex = `${perm.marketIndex}-${perm.itemCodeIndex}`;
                // const demandCommonIndex = `${perm.market}-${perm.itemCode}`;
                if (!commonDemands[demandCommonIndex]) {
                    commonDemands[demandCommonIndex] = {
                        constraint: Number(perm.demand),
                        indicies: [index],
                        text: `${perm.variable}`,
                        total: 0,
                        market: perm.market,
                        item: perm.itemCode,
                    };
                } else {
                    commonDemands[demandCommonIndex].indicies.push(index);
                    commonDemands[demandCommonIndex].text += ` + ${perm.variable}`;
                }

                const capacityCommonIndex = `${perm.lineIndex}`;
                // const capacityCommonIndex = `${perm.line}`;
                if (!commonCapacity[capacityCommonIndex]) {
                    commonCapacity[capacityCommonIndex] = {
                        constraint: Number(perm.max_capacity),
                        indicies: [index],
                        text: `${perm.capacity}*${perm.variable}`,
                        total: 0,
                        line: perm.line,
                    };
                } else {
                    commonCapacity[capacityCommonIndex].indicies.push(index);
                    commonCapacity[capacityCommonIndex].text += ` + ${perm.capacity}*${perm.variable}`;
                }

                const prepCapacityCommonIndex = `${perm.prepIndex}`;
                if (!commonPrepCapacity[prepCapacityCommonIndex]) {
                    commonPrepCapacity[prepCapacityCommonIndex] = {
                        constraint: Number(perm.prep_max_capacity),
                        indicies: [index],
                        text: `${perm.prepCapacity}*${perm.variable}`,
                        total: 0,
                        prep: perm.prep,
                    };
                } else {
                    commonPrepCapacity[prepCapacityCommonIndex].indicies.push(index);
                    commonPrepCapacity[prepCapacityCommonIndex].text += ` + ${perm.prepCapacity}*${perm.variable}`;
                }
            }

            const newOutput2 = Object.values(commonDemands).reduce((prev, curr) => {
                if (prev === "") return `${curr.text} <= ${curr.constraint}`;
                return `${prev}\n${curr.text} <= ${curr.constraint}`;
            }, "");

            const newOutput3 = Object.values(commonCapacity).reduce((prev, curr) => {
                if (prev === "") return `${curr.text} <= ${curr.constraint}`;
                return `${prev}\n${curr.text} <= ${curr.constraint}`;
            }, "");

            const newOutput4 = Object.values(commonPrepCapacity).reduce((prev, curr) => {
                if (prev === "") return `${curr.text} <= ${curr.constraint}`;
                return `${prev}\n${curr.text} <= ${curr.constraint}`;
            }, "");

            setOutput1(newOutput1);
            setOutput2(newOutput2);
            setOutput3(newOutput3);
            setOutput4(newOutput4);

            //solver from env
            const res = await fetch(process.env.REACT_APP_SOLVER_URL ?? "https://solver.muqassim.com/solve", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    // Send the LP problem data here
                    // Replace this with your specific data format
                    perms: permArray,
                    demandConstraints: Object.values(commonDemands).map((cd) => ({
                        relatedIndices: cd.indicies,
                        demand: cd.constraint,
                    })),
                    capacityConstraints: Object.values(commonCapacity).map((cc) => ({
                        relatedIndices: cc.indicies.map((cci) => ({
                            index: cci,
                            capacity: Number(permArray[cci].capacity),
                        })),
                        capacity: cc.constraint,
                    })),
                    prepCapacityConstraints: Object.values(commonPrepCapacity).map((cc) => ({
                        relatedIndices: cc.indicies.map((cci) => ({
                            index: cci,
                            capacity: Number(permArray[cci].prepCapacity),
                        })),
                        capacity: cc.constraint,
                    })),
                }),
            });

            const data = await res.json();

            const newCombinations = _.cloneDeep(combinations);
            const xValues: { [key: string]: number } = data.values;
            // console.log(xValues, newCombinations);

            //calculate line utilization
            const linesUtil: { [line: string]: number } = {};
            const prepUtil: { [prep: string]: number } = {};

            Object.keys(xValues).forEach((val: string) => {
                const comb = newCombinations[val];
                if (!comb) return;
                //assign x value
                comb.variableValue = xValues[val];
                //assign lines and accumilative
                if (!linesUtil[comb.line]) linesUtil[comb.line] = 0;
                linesUtil[comb.line] +=
                    ((Number(comb.capacity) ?? 0) * comb.variableValue) / (Number(comb.max_capacity) ?? 1);
                //assign preps and accumilative
                if (comb.prep) {
                    if (!prepUtil[comb.prep]) prepUtil[comb.prep] = 0;
                    prepUtil[comb.prep] +=
                        ((Number(comb.prepCapacity) || 0) * comb.variableValue) / (Number(comb.prep_max_capacity) || 1);
                }

                //calculate the total for the unmet demands
                commonDemands[`${comb.marketIndex}-${comb.itemCodeIndex}`].total += comb.variableValue;
            });

            // console.log(commonDemands);

            const unmetDemands = Object.keys(commonDemands).reduce((prev: UnmetDemands[], curr: string) => {
                const demand = commonDemands[curr];
                if (demand.constraint <= demand.total) return prev;

                prev.push({
                    market: demand.market ?? "Unknown",
                    item: demand.item ?? "Unknown",
                    demand: demand.constraint,
                    unmet: demand.constraint - demand.total,
                });
                return prev;
            }, []);

            const graphData: UserSerie<BarData>[] = [{ label: "Utilization %", data: [] }];

            graphData[0].data = Object.keys(linesUtil).map((line): BarData => {
                return {
                    primary: line,
                    secondary: linesUtil[line] * 100,
                    radius: undefined,
                };
            });

            const prepGraphData: UserSerie<BarData>[] = [{ label: "Prep. Utilization %", data: [] }];

            prepGraphData[0].data = Object.keys(prepUtil).map((prep): BarData => {
                return {
                    primary: prep,
                    secondary: prepUtil[prep] * 100,
                    radius: undefined,
                };
            });

            if (prepGraphData[0].data.length === 0) {
                prepGraphData[0].data.push({ primary: "No Data", secondary: 0, radius: undefined });
            }

            setLineUtilGraphData(graphData);
            setPrepUtilGraphData(prepGraphData);
            setUnmetDemands(unmetDemands);
            exportExcel({
                combinations: Object.values(newCombinations),
                optimalValue: data.optimal_value,
                unmetDemands,
                linesUtil,
                prepUtil,
                demands: commonDemands,
            });
            setCombinations(newCombinations);
            setResult(data);
        } catch (error) {
            console.error(error);
        }
        setExportLoading(false);
    };

    const onRowsChange = (updatedRows: Combination[], data: RowsChangeData<Combination, unknown>) => {
        const index = data.indexes[0];
        const newUpdatedRows = _.cloneDeep(updatedRows);
        const updatedRow = newUpdatedRows[index];

        switch (data.column.key) {
            case "prep":
                newUpdatedRows.forEach((row) => {
                    if (row.prepIndex === updatedRow.prepIndex) row.prep = `${updatedRow.prep}`.trim();
                });
                break;
            case "line":
                newUpdatedRows.forEach((row) => {
                    if (row.lineIndex === updatedRow.lineIndex) row.line = `${updatedRow.line}`.trim();
                });
                break;
            case "market":
                newUpdatedRows.forEach((row) => {
                    if (row.marketIndex === updatedRow.marketIndex) row.market = `${updatedRow.market}`.trim();
                });
                break;
            case "itemCode":
                newUpdatedRows.forEach((row) => {
                    if (row.itemCodeIndex === updatedRow.itemCodeIndex) row.itemCode = `${updatedRow.itemCode}`.trim();
                });
                break;
            case "description":
                newUpdatedRows.forEach((row) => {
                    if (row.itemCodeIndex === updatedRow.itemCodeIndex)
                        row.description = `${updatedRow.description}`.trim();
                });
                break;
            case "variable":
                const isDublicateExist = newUpdatedRows.some((row, rowIndex) => {
                    if (index === rowIndex) return false;
                    if (row.variable.toLocaleLowerCase() === updatedRow.variable.toLocaleLowerCase()) return true;
                    return false;
                });
                if (isDublicateExist) return;
                break;
            case "profit":
                if (isNaN(Number(updatedRow[data.column.key as keyof Combination]))) return;
                break;
            case "demand":
                if (isNaN(Number(updatedRow[data.column.key as keyof Combination]))) return;
                newUpdatedRows.forEach((row) => {
                    if (row.marketIndex === updatedRow.marketIndex && row.itemCodeIndex === updatedRow.itemCodeIndex)
                        row.demand = `${updatedRow.demand}`.trim();
                });
                break;
            case "prepCapacity":
                if (isNaN(Number(updatedRow[data.column.key as keyof Combination]))) return;
                newUpdatedRows.forEach((row) => {
                    if (row.prepIndex === updatedRow.prepIndex && row.itemCodeIndex === updatedRow.itemCodeIndex)
                        row.prepCapacity = `${updatedRow.prepCapacity}`.trim();
                });
                break;
            case "capacity":
                if (isNaN(Number(updatedRow[data.column.key as keyof Combination]))) return;
                newUpdatedRows.forEach((row) => {
                    if (row.lineIndex === updatedRow.lineIndex && row.itemCodeIndex === updatedRow.itemCodeIndex)
                        row.capacity = `${updatedRow.capacity}`.trim();
                });
                break;
            case "prep_max_capacity":
                if (isNaN(Number(updatedRow[data.column.key as keyof Combination]))) return;
                newUpdatedRows.forEach((row) => {
                    if (row.prepIndex === updatedRow.prepIndex)
                        row.prep_max_capacity = `${updatedRow.prep_max_capacity}`.trim();
                });
                break;
            case "max_capacity":
                if (isNaN(Number(updatedRow[data.column.key as keyof Combination]))) return;
                newUpdatedRows.forEach((row) => {
                    if (row.lineIndex === updatedRow.lineIndex) row.max_capacity = `${updatedRow.max_capacity}`.trim();
                });
                break;
        }

        const newCombinations = newUpdatedRows.reduce((prev, curr) => ({ ...prev, [curr.variable]: curr }), {});
        setCombinations(newCombinations);
    };

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>, ref: MutableRefObject<HTMLInputElement>) => {
        if (e.target === null || e.target.files === null || ref === null) return;
        try {
            setImportLoading(true);
            const file = e.target.files[0];

            if (file) {
                const data = await readExcel(file);
                const newCombinations = data.reduce((prev: { [key: string]: Combination }, curr: Combination) => {
                    prev[curr.variable] = curr;
                    return prev;
                }, {});
                setCombinations(newCombinations);
                // Clear the input value
                if (ref.current) {
                    ref.current.value = "";
                }
            }
        } catch (error) {
            console.error(error);
        }
        setImportLoading(false);
    };

    // console.log("linesUtilGraphData", linesUtilGraphData);
    // console.log("prepsUtilGraphData", prepsUtilGraphData);

    return {
        //vars
        columns,
        unmetDemandsColumns,
        //states
        combinations,
        linesUtilGraphData,
        prepsUtilGraphData,
        unmetDemands,
        output1,
        output2,
        output3,
        output4,
        result,
        isFormulationsHidden,

        //loading
        importLoading,
        exportLoading,

        //functions
        generateCombinations,
        generateOutputs,
        onRowsChange,
        handleFileChange,
        setIsFormulationsHidden,
    };
};

export default useCombinationsLogic;
